import { Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, articles } from '@/mocks/articles';

export default function Home() {
  const latestArticles = articles.slice(0, 6);
  const faqArticles = articles.filter((a) => a.category === 'changjian-wenti').slice(0, 4);

  return (
    <>
      <PageMeta
        title="海角社区地址——官方入口与最新访问指南"
        description="找不到海角社区地址？本页整理海角社区地址的最新官方入口、镜像备用链接及访问常见问题解答，帮助用户快速、稳定地访问海角社区，收藏本页随时获取最新地址更新。"
        canonicalUrl="https://haijiao-navi.readdy.ai/"
        keywords="海角社区地址,海角社区最新地址,海角社区官网入口,海角社区镜像地址,海角社区备用链接,海角社区访问教程,海角社区网址导航"
        jsonLdScripts={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '海角社区地址——官方入口与最新访问指南',
            description: '找不到海角社区地址？本页整理海角社区地址的最新官方入口、镜像备用链接及访问常见问题解答。',
            url: 'https://haijiao-navi.readdy.ai/',
            isPartOf: {
              '@type': 'WebSite',
              name: '海角社区地址导航',
              url: 'https://haijiao-navi.readdy.ai/',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqArticles.map((faq) => ({
              '@type': 'Question',
              name: faq.title,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.excerpt,
              },
            })),
          },
        ]}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20modern%20digital%20community%20hub%20entrance%20with%20soft%20amber%20and%20cream%20color%20palette%2C%20abstract%20flowing%20geometric%20shapes%20connecting%20together%2C%20clean%20minimalist%20design%2C%20warm%20gradient%20background%20with%20subtle%20texture%2C%20friendly%20welcoming%20atmosphere%2C%20digital%20navigation%20concept%2C%20high%20quality%20editorial%20style%20with%20soft%20diffused%20lighting&width=1600&height=900&seq=hero-haijiao-2026&orientation=landscape"
            alt="海角社区地址导航"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/30"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                海角社区地址——官方入口与最新访问指南
              </h1>
              <p className="text-base md:text-lg text-white/85 mb-8 max-w-2xl mx-auto leading-relaxed">
                一站式整理海角社区最新官方入口、镜像备用链接及访问教程，帮助您快速稳定访问海角社区。收藏本站，随时获取最新地址更新。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/guanfang-rukou"
                  className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-arrow-right-circle-line mr-2"></i>
                  查看官方入口
                </Link>
                <Link
                  to="/category/changjian-wenti"
                  className="w-full sm:w-auto px-8 py-3.5 bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-center backdrop-blur-sm"
                >
                  <i className="ri-question-answer-line mr-2"></i>
                  常见问题解答
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Category Quick Links */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-2">
              快速导航入口
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              选择您需要的分类，快速找到相关内容
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200/80 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-500 mt-1 text-center">
                    {cat.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  最新文章
                </h2>
                <p className="text-sm text-foreground-600">
                  持续更新海角社区相关的最新指南和教程
                </p>
              </div>
              <Link
                to="/category/fangwen-jiaocheng"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              关于海角社区访问和使用的高频问题解答
            </p>
            <div className="space-y-3">
              {faqArticles.map((faq) => (
                <details
                  key={faq.slug}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.title}
                    </span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-300">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.excerpt}
                    </p>
                    <Link
                      to={`/article/${faq.slug}`}
                      className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                    >
                      查看详细解答 →
                    </Link>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 bg-accent-500">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              收藏本站，随时获取最新地址
            </h2>
            <p className="text-white/85 text-base mb-8 leading-relaxed max-w-xl mx-auto">
              海角社区地址可能会不定期更新，建议将本站加入浏览器收藏夹，确保随时都能找到最新可用入口。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/guanfang-rukou"
                className="w-full sm:w-auto px-8 py-3.5 bg-white text-accent-600 hover:bg-white/90 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
              >
                <i className="ri-star-line mr-2"></i>
                立即查看入口
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3.5 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-center border border-white/20"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}